import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { postsAPI } from '../services/api'
import { Post } from '../types'
import VideoPlayer from '../components/VideoPlayer'
import AudioPlayer from '../components/AudioPlayer'
import './PostDetail.css'

const PostDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>()
  const [post, setPost] = useState<Post | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (slug) {
      loadPost(slug)
    }
  }, [slug])

  const loadPost = async (postSlug: string) => {
    try {
      setIsLoading(true)
      setError(null)
      
      // Check if we've already viewed this post in this session
      const viewedPosts = JSON.parse(sessionStorage.getItem('viewedPosts') || '[]')
      const alreadyViewed = viewedPosts.includes(postSlug)
      
      const data = await postsAPI.getById(postSlug)
      setPost(data.post)
      
      // Mark as viewed in session storage to prevent duplicate tracking on reload
      if (!alreadyViewed) {
        viewedPosts.push(postSlug)
        sessionStorage.setItem('viewedPosts', JSON.stringify(viewedPosts))
      }
      
    } catch (error) {
      console.error('Failed to load post:', error)
      setError('Không tìm thấy bài viết')
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    if (hours > 0) {
      return `${hours}:${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`
    }
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`
  }

  if (isLoading) {
    return (
      <div className="post-detail-loading">
        <div className="loading">Đang tải bài viết...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="post-detail-error">
        <div className="error-content">
          <h2>Lỗi</h2>
          <p>{error}</p>
          <Link to="/" className="back-home">← Về trang chủ</Link>
        </div>
      </div>
    )
  }

  if (!post) {
    return (
      <div className="post-detail-error">
        <div className="error-content">
          <h2>Không tìm thấy bài viết</h2>
          <p>Bài viết bạn đang tìm kiếm không tồn tại hoặc đã bị xóa.</p>
          <Link to="/" className="back-home">← Về trang chủ</Link>
        </div>
      </div>
    )
  }

  // Render content based on media type
  const renderContent = () => {
    if (post.media_type === 'video') {
      return renderVideoContent()
    } else if (post.media_type === 'podcast') {
      return renderPodcastContent()
    } else {
      return renderTextContent()
    }
  }

  const renderVideoContent = () => (
    <div className="post-detail video-detail">
      <div className="container">
        <article className="video-article">
          {/* Video Header */}
          <header className="video-header">
            <nav className="breadcrumb">
              <Link to="/">Trang chủ</Link>
              <span>/</span>
              <Link to={`/category/${post.category_slug}`}>{post.category_name}</Link>
              <span>/</span>
              <span>{post.title}</span>
            </nav>
          </header>

          {/* Video Player */}
          <div className="video-player-container">
            <VideoPlayer
              src={post.media_url || ''}
              poster={post.featured_image}
              title={post.title}
            />
          </div>

          {/* Video Info */}
          <div className="video-info">
            <div className="video-category">
              <Link to={`/category/${post.category_slug}`} className="category-link">
                {post.category_name}
              </Link>
            </div>
            
            <h1 className="video-title">{post.title}</h1>
            
            <div className="video-meta">
              <div className="meta-left">
                <span className="publish-date">{formatDate(post.publish_date)}</span>
                {post.media_duration && (
                  <span className="video-duration">
                    ⏱️ {formatDuration(post.media_duration)}
                  </span>
                )}
              </div>
              <div className="meta-right">
                <span className="view-count">👁 {post.view_count} lượt xem</span>
                {post.is_featured && (
                  <span className="featured-badge">Nổi bật</span>
                )}
              </div>
            </div>

            <div className="author-info">
              <span className="author">Tác giả: <strong>{post.author_name}</strong></span>
            </div>
          </div>

          {/* Video Description */}
          {(post.excerpt || post.content) && (
            <div className="video-description">
              <h3>Mô tả</h3>
              {post.excerpt && (
                <div className="video-excerpt">
                  {post.excerpt}
                </div>
              )}
              {post.content && (
                <div 
                  className="video-content"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              )}
            </div>
          )}

          {/* Video Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="video-tags">
              <h4>Từ khóa:</h4>
              <div className="tags-list">
                {post.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Video Footer */}
          <footer className="video-footer">
            <div className="video-stats">
              <span className="publish-info">
                Xuất bản: {formatDate(post.publish_date)}
              </span>
              {post.updated_at !== post.created_at && (
                <span className="update-info">
                  Cập nhật: {formatDate(post.updated_at)}
                </span>
              )}
            </div>
            
            <div className="video-actions">
              <button 
                className="share-btn"
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: post.title,
                      url: window.location.href
                    })
                  } else {
                    navigator.clipboard.writeText(window.location.href)
                    alert('Đã sao chép liên kết video!')
                  }
                }}
              >
                📤 Chia sẻ
              </button>
              
              <Link to="/" className="back-home">
                ← Về trang chủ
              </Link>
            </div>
          </footer>
        </article>
      </div>
    </div>
  )

  const renderPodcastContent = () => (
    <div className="post-detail podcast-detail">
      <div className="container">
        <article className="podcast-article">
          {/* Podcast Header */}
          <header className="podcast-header">
            <nav className="breadcrumb">
              <Link to="/">Trang chủ</Link>
              <span>/</span>
              <Link to={`/category/${post.category_slug}`}>{post.category_name}</Link>
              <span>/</span>
              <span>{post.title}</span>
            </nav>
          </header>

          {/* Podcast Player */}
          <div className="podcast-player-container">
            <div className="podcast-banner">
              <div className="podcast-artwork">
                {post.featured_image && (
                  <img src={post.featured_image} alt={post.title} />
                )}
              </div>
              <div className="podcast-summary">
                <div className="podcast-category">
                  <Link to={`/category/${post.category_slug}`} className="category-link">
                    {post.category_name}
                  </Link>
                </div>
                <h1 className="podcast-title">{post.title}</h1>
                <div className="podcast-meta">
                  <span className="publish-date">📅 {formatDate(post.publish_date)}</span>
                  {post.media_duration && (
                    <span className="podcast-duration">
                      ⏱️ {formatDuration(post.media_duration)}
                    </span>
                  )}
                  <span className="view-count">👁 {post.view_count} lượt nghe</span>
                </div>
                <div className="author-info">
                  <span className="author">Người dẫn: <strong>{post.author_name}</strong></span>
                </div>
              </div>
            </div>
            
            <div className="audio-player-wrapper">
              <AudioPlayer
                src={post.media_url || ''}
                title={post.title}
                artwork={post.featured_image}
              />
            </div>
          </div>

          {/* Podcast Description */}
          {(post.excerpt || post.content) && (
            <div className="podcast-description">
              <h3>Mô tả chương trình</h3>
              {post.excerpt && (
                <div className="podcast-excerpt">
                  {post.excerpt}
                </div>
              )}
              {post.content && (
                <div 
                  className="podcast-content"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              )}
            </div>
          )}

          {/* Podcast Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="podcast-tags">
              <h4>Chủ đề:</h4>
              <div className="tags-list">
                {post.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Podcast Footer */}
          <footer className="podcast-footer">
            <div className="podcast-stats">
              <span className="publish-info">
                Phát sóng: {formatDate(post.publish_date)}
              </span>
              {post.updated_at !== post.created_at && (
                <span className="update-info">
                  Cập nhật: {formatDate(post.updated_at)}
                </span>
              )}
            </div>
            
            <div className="podcast-actions">
              <button 
                className="share-btn"
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: post.title,
                      url: window.location.href
                    })
                  } else {
                    navigator.clipboard.writeText(window.location.href)
                    alert('Đã sao chép liên kết podcast!')
                  }
                }}
              >
                📤 Chia sẻ
              </button>
              
              <button 
                className="subscribe-btn"
                onClick={() => {
                  alert('Tính năng đăng ký sẽ được cập nhật sớm!')
                }}
              >
                🔔 Đăng ký
              </button>
              
              <Link to="/" className="back-home">
                ← Về trang chủ
              </Link>
            </div>
          </footer>
        </article>
      </div>
    </div>
  )

  const renderTextContent = () => (
    <div className="post-detail">
      <div className="container">
        <article className="post-article">
          {/* Article Header */}
          <header className="article-header">
            <nav className="breadcrumb">
              <Link to="/">Trang chủ</Link>
              <span>/</span>
              <Link to={`/category/${post.category_slug}`}>{post.category_name}</Link>
              <span>/</span>
              <span>{post.title}</span>
            </nav>
            
            <div className="article-category">
              <Link to={`/category/${post.category_slug}`} className="category-link">
                {post.category_name}
              </Link>
            </div>
            
            <h1 className="article-title">{post.title}</h1>
            
            {post.excerpt && (
              <div className="article-excerpt">
                {post.excerpt}
              </div>
            )}
            
            <div className="article-meta">
              <div className="meta-left">
                <span className="author">Tác giả: <strong>{post.author_name}</strong></span>
                <span className="publish-date">{formatDate(post.publish_date)}</span>
              </div>
              <div className="meta-right">
                <span className="view-count">👁 {post.view_count} lượt xem</span>
                {post.is_featured && (
                  <span className="featured-badge">Nổi bật</span>
                )}
              </div>
            </div>
          </header>

          {/* Featured Image */}
          {post.featured_image && (
            <div className="article-image">
              <img src={post.featured_image} alt={post.title} />
            </div>
          )}

          {/* Article Content */}
          <div className="article-content">
            <div 
              className="content-body"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>

          {/* Article Tags */}
          {post.tags && post.tags.length > 0 && (
            <div className="article-tags">
              <h4>Từ khóa:</h4>
              <div className="tags-list">
                {post.tags.map((tag, index) => (
                  <span key={index} className="tag">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Article Footer */}
          <footer className="article-footer">
            <div className="article-stats">
              <span className="publish-info">
                Xuất bản: {formatDate(post.publish_date)}
              </span>
              {post.updated_at !== post.created_at && (
                <span className="update-info">
                  Cập nhật: {formatDate(post.updated_at)}
                </span>
              )}
            </div>
            
            <div className="article-actions">
              <button 
                className="share-btn"
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: post.title,
                      url: window.location.href
                    })
                  } else {
                    navigator.clipboard.writeText(window.location.href)
                    alert('Đã sao chép liên kết bài viết!')
                  }
                }}
              >
                📤 Chia sẻ
              </button>
              
              <Link to="/" className="back-home">
                ← Về trang chủ
              </Link>
            </div>
          </footer>
        </article>
      </div>
    </div>
  )

  return renderContent()
}

export default PostDetail